<template>
  <div class="rich-text-card">
    <div class="header">
      <div v-if="hasIcon" class="icon">
        <slot name="icon"></slot>
      </div>

      <div v-if="showTitle" class="title" :title="title">{{ title }}</div>
      <slot name="info"></slot>
    </div>
    <div class="content">
      <rich-text-wrapper
        :content="content"
        :expandable="expandable"
        :visible="visible"
        :truncation-type="truncationType"
        :config="richTextConfig"
        :copyright-protected="isPrintViewPage"
        :anchor="anchor"
        :resource-type="resourceType"
        :annotation-resource-id="annotationResourceId"
        :can-annotate="canAnnotate"
        :allow-ask-salis="allowAskSalis"
        :can-ask-salis="canAskSalis"
        :is-ask-salis-supported-component="isAskSalisSupportedComponent"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, useSlots } from "vue";
import RichTextWrapper from "@/plugins/richtext/components/RichTextWrapper.vue";

interface Props {
  title?: string;
  content?: string;
  expandable?: boolean;
  visible?: boolean;
  truncationType?: string;
  richTextConfig?: object;
  isPrintViewPage?: boolean;
  anchor?: string;
  resourceType?: string;
  annotationResourceId?: string;
  canAnnotate?: boolean;
  allowAskSalis?: boolean;
  canAskSalis?: boolean;
  isAskSalisSupportedComponent?: boolean;
}
const {
  title = "",
  content = "",
  expandable = true,
  visible = true,
  truncationType = "primary",
  richTextConfig = {} as Record<string, unknown>,
  isPrintViewPage = false,
  anchor = "",
  resourceType = "",
  annotationResourceId = "",
  canAnnotate = false,
  allowAskSalis = false,
  canAskSalis = false,
  isAskSalisSupportedComponent = false,
} = defineProps<Props>();

const slots = useSlots();
const showTitle = computed(() => title.length > 0);
const hasIcon = computed(() => !!slots.icon);
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.rich-text-card {
  padding: $spacing-05;
  background: $ui-01;
  color: $text-01;
  border-radius: 10px;

  > .header {
    @include carbon--type-style("productive-heading-01", true);
    display: flex;
    align-items: center;
    font-weight: 700;

    .title {
      margin-bottom: 0;
    }

    > .icon {
      display: flex;
      align-items: center;
      margin-right: $spacing-03;
    }
  }

  > .content {
    @include carbon--type-style("body-short-01", true);
    padding: $spacing-02 0 0;
  }
}
</style>
